import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const AbsenceCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date(2026, 8, 1)); // September 2026

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  // Adjust so Monday is 0, Sunday is 6
  const startingDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  // Mock events matching the mockup
  const events = {
    14: { type: 'annual', color: 'bg-violet-500' },
    15: { type: 'annual', color: 'bg-violet-500' },
    16: { type: 'annual', color: 'bg-violet-500' },
    17: { type: 'annual', color: 'bg-violet-500' }, // selected in mockup
    22: { type: 'sick', color: 'bg-emerald-500' },
    30: { type: 'other', color: 'bg-red-500' },
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const renderDays = () => {
    const days = [];
    const prevMonthDays = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();

    // Previous month days
    for (let i = 0; i < startingDay; i++) {
      days.push(
        <div key={`prev-${i}`} className="h-10 flex items-center justify-center text-sm text-gray-300 font-medium">
          {prevMonthDays - startingDay + i + 1}
        </div>
      );
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const hasEvent = events[i];
      const isSelected = i === 17 && currentDate.getMonth() === 8; // Highlight 17th of Sept
      const isSick = i === 22 && currentDate.getMonth() === 8;
      const isOther = i === 30 && currentDate.getMonth() === 8;
      const isAnnual = (i >= 14 && i <= 17) && currentDate.getMonth() === 8;

      let cellClass = "h-10 flex flex-col items-center justify-center relative text-sm font-medium rounded-full cursor-pointer transition-colors hover:bg-gray-100 text-gray-700";
      
      if (isSelected) {
        cellClass = "h-10 flex flex-col items-center justify-center relative text-sm font-bold rounded-full cursor-pointer transition-colors bg-violet-100 text-violet-700";
      } else if (isSick) {
        cellClass = "h-10 flex flex-col items-center justify-center relative text-sm font-medium rounded-full cursor-pointer transition-colors bg-emerald-50 text-emerald-700";
      } else if (isOther) {
        cellClass = "h-10 flex flex-col items-center justify-center relative text-sm font-medium rounded-full cursor-pointer transition-colors bg-red-50 text-red-700";
      } else if (isAnnual) {
        cellClass = "h-10 flex flex-col items-center justify-center relative text-sm font-medium rounded-full cursor-pointer transition-colors bg-violet-50 text-violet-700";
      }

      days.push(
        <div key={`curr-${i}`} className={cellClass}>
          {i}
          {hasEvent && (
            <div className={`absolute top-1 right-2 w-1.5 h-1.5 rounded-full ${hasEvent.color}`}></div>
          )}
        </div>
      );
    }

    // Next month days
    const totalCells = days.length;
    const remainingCells = 42 - totalCells; // 6 rows of 7 days
    for (let i = 1; i <= remainingCells; i++) {
      days.push(
        <div key={`next-${i}`} className="h-10 flex items-center justify-center text-sm text-gray-300 font-medium">
          {i}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 w-full">
      <h3 className="font-bold text-gray-900 mb-4">Calendrier des absences</h3>
      
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-1">
          <button onClick={handlePrevMonth} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200">
            <ChevronLeft size={16} />
          </button>
          <button onClick={handleNextMonth} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200">
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="text-sm font-bold text-gray-900">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </div>
        <button className="text-xs font-semibold text-gray-700 bg-white border border-gray-200 px-3 py-1.5 rounded-lg shadow-sm hover:bg-gray-50 transition-colors">
          Aujourd'hui
        </button>
      </div>

      <div className="grid grid-cols-7 gap-y-2 gap-x-1 mb-6 text-center">
        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day, idx) => (
          <div key={day} className={`text-xs font-semibold ${idx === 6 || idx === 5 ? 'text-gray-400' : 'text-violet-600'}`}>
            {day}
          </div>
        ))}
        {renderDays()}
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-violet-500"></div>
          <span className="text-gray-600 font-medium">Congé annuel</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
          <span className="text-gray-600 font-medium">Congé maladie</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-orange-400"></div>
          <span className="text-gray-600 font-medium">En attente</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
          <span className="text-gray-600 font-medium">Autre absence</span>
        </div>
      </div>
    </div>
  );
};

export default AbsenceCalendar;
